import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Trash2 } from 'lucide-react';
import Modal from '../Common/Modal';
import Button from '../Common/Button';
import {
  getAssignableRoles,
  getAdminUserGroups,
  createAdminUserGroup,
  updateAdminUserGroup,
  deleteAdminUserGroup,
} from '../../constants';

const FALLBACK_ROLES = [
  { value: 'Employee', label: 'Employee' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Manager', label: 'Manager' },
  { value: 'Vice President', label: 'Vice President' },
  { value: 'Scrum Master', label: 'Scrum Master' },
];

const emptyMember = (defaultRole = 'Employee') => ({
  email: '',
  role: defaultRole,
});

const AddEmployeeToProjectModal = ({
  isOpen,
  onClose,
  project,
  companyId,
  onInvite,
  onApplyGroup,
  assigning = false,
  allowUserGroups = false,
}) => {
  const [mode, setMode] = useState('single'); // single | group
  const [groupMode, setGroupMode] = useState('apply'); // apply | create
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [roleOptions, setRoleOptions] = useState(FALLBACK_ROLES);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [members, setMembers] = useState([emptyMember()]);
  const [savingGroup, setSavingGroup] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);

  const projectName = project?.projectName || project?.name || 'Project';
  const projectId = project?._id || project?.projectId;
  const defaultRole = roleOptions[0]?.value || 'Employee';

  const selectedGroup = useMemo(
    () => groups.find((g) => String(g._id) === String(selectedGroupId)) || null,
    [groups, selectedGroupId],
  );

  const loadGroups = useCallback(async () => {
    if (!allowUserGroups || !companyId) return;
    setLoadingGroups(true);
    try {
      const res = await getAdminUserGroups(companyId);
      const list = res?.data || res || [];
      setGroups(Array.isArray(list) ? list : []);
    } catch {
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, [allowUserGroups, companyId]);

  useEffect(() => {
    if (!isOpen) {
      setMode('single');
      setGroupMode('apply');
      setEmail('');
      setError('');
      setSelectedGroupId('');
      setGroupName('');
      setGroupDescription('');
      setMembers([emptyMember()]);
      setEditingGroupId(null);
      return undefined;
    }

    let cancelled = false;
    const loadRoles = async () => {
      setLoadingRoles(true);
      try {
        const res = await getAssignableRoles(companyId || undefined);
        if (cancelled) return;
        const list = res?.data || res || [];
        const options = (Array.isArray(list) ? list : [])
          .map((r) => ({
            value: r.name || r.value,
            label: r.name || r.label,
          }))
          .filter((r) => r.value);
        if (options.length) {
          setRoleOptions(options);
          const preferEmployee = options.find(
            (o) => String(o.value).toLowerCase() === 'employee',
          );
          const nextRole = preferEmployee?.value || options[0].value;
          setRole(nextRole);
          setMembers([emptyMember(nextRole)]);
        } else {
          setRoleOptions(FALLBACK_ROLES);
          setRole('Employee');
          setMembers([emptyMember('Employee')]);
        }
      } catch {
        if (!cancelled) {
          setRoleOptions(FALLBACK_ROLES);
          setRole('Employee');
          setMembers([emptyMember('Employee')]);
        }
      } finally {
        if (!cancelled) setLoadingRoles(false);
      }
    };

    loadRoles();
    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [isOpen, loadGroups, companyId]);

  const handleSingleSubmit = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Enter a valid email address.');
      return;
    }
    if (!role) {
      setError('Select a role.');
      return;
    }
    setError('');
    await onInvite({
      email: trimmed,
      role,
      companyId,
      projectId,
    });
  };

  const handleApplyGroup = async () => {
    if (!selectedGroupId) {
      setError('Select a user group.');
      return;
    }
    setError('');
    await onApplyGroup({
      groupId: selectedGroupId,
      companyId,
      projectId,
    });
  };

  const startCreateGroup = () => {
    setGroupMode('create');
    setEditingGroupId(null);
    setGroupName('');
    setGroupDescription('');
    setMembers([emptyMember(defaultRole)]);
    setError('');
  };

  const startEditGroup = (group) => {
    setGroupMode('create');
    setEditingGroupId(group._id);
    setGroupName(group.name || '');
    setGroupDescription(group.description || '');
    setMembers(
      (group.members || []).length
        ? group.members.map((m) => ({
            email: m.email || '',
            role: m.role || defaultRole,
          }))
        : [emptyMember(defaultRole)],
    );
    setError('');
  };

  const validateMembers = () => {
    const cleaned = members
      .map((m) => ({
        email: String(m.email || '')
          .trim()
          .toLowerCase(),
        role: String(m.role || '').trim(),
      }))
      .filter((m) => m.email || m.role);

    if (!cleaned.length) {
      setError('Add at least one member with email and role.');
      return null;
    }
    for (const m of cleaned) {
      if (!m.email.includes('@') || !m.role) {
        setError('Each member needs a valid email and role.');
        return null;
      }
    }
    return cleaned;
  };

  const handleSaveGroup = async ({ alsoApply }) => {
    const trimmedName = groupName.trim();
    if (!trimmedName) {
      setError('Enter a group name.');
      return;
    }
    const cleanedMembers = validateMembers();
    if (!cleanedMembers) return;

    setError('');
    setSavingGroup(true);
    try {
      let groupId = editingGroupId;
      if (editingGroupId) {
        await updateAdminUserGroup(editingGroupId, {
          name: trimmedName,
          description: groupDescription.trim(),
          members: cleanedMembers,
        });
      } else {
        const created = await createAdminUserGroup({
          name: trimmedName,
          companyId,
          description: groupDescription.trim(),
          members: cleanedMembers,
          sourceProjectId: projectId,
        });
        groupId = created?.data?._id || created?._id;
      }

      await loadGroups();

      if (alsoApply && groupId) {
        await onApplyGroup({
          groupId,
          companyId,
          projectId,
        });
        return;
      }

      setGroupMode('apply');
      setSelectedGroupId(String(groupId || ''));
      setEditingGroupId(null);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save user group.');
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroupId) return;
    if (!window.confirm('Delete this user group? Projects already assigned are not affected.')) {
      return;
    }
    setSavingGroup(true);
    setError('');
    try {
      await deleteAdminUserGroup(selectedGroupId);
      setSelectedGroupId('');
      await loadGroups();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to delete user group.');
    } finally {
      setSavingGroup(false);
    }
  };

  const updateMember = (index, field, value) => {
    setMembers((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    );
  };

  const busy = assigning || savingGroup;

  const singleContent = (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-[#0A2342] dark:text-white mb-1.5">
          Email
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Type employee email"
          autoComplete="off"
          className="w-full px-3 py-2.5 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB]"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-[#0A2342] dark:text-white mb-1.5">
          Role
        </label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={loadingRoles || !roleOptions.length}
          className="w-full px-3 py-2.5 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB] disabled:opacity-60"
        >
          {loadingRoles && <option value="">Loading roles...</option>}
          {!loadingRoles &&
            roleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
        </select>
        <p className="mt-1 text-xs text-[#6B7C93] dark:text-[#8FA3B8]">
          Roles come from Admin → Roles &amp; Permissions.
        </p>
      </div>

      <p className="text-xs text-[#6B7C93] dark:text-[#8FA3B8]">
        An invitation email will be sent with login credentials (default password included for
        new users). They will see this project on Standup.
      </p>
    </div>
  );

  const groupApplyContent = (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-[#6B7C93] dark:text-[#8FA3B8]">
          Reuse a saved group to add the same people to <b className="text-[#0A2342] dark:text-white">{projectName}</b>.
        </p>
        <button
          type="button"
          onClick={startCreateGroup}
          className="shrink-0 text-xs font-medium text-[#326AEB] dark:text-[#78B5FF] hover:underline"
        >
          + Create group
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#0A2342] dark:text-white mb-1.5">
          User group
        </label>
        <select
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          disabled={loadingGroups || busy}
          className="w-full px-3 py-2.5 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB] disabled:opacity-60"
        >
          <option value="">
            {loadingGroups ? 'Loading groups...' : 'Select a user group'}
          </option>
          {groups.map((g) => (
            <option key={g._id} value={g._id}>
              {g.name} ({(g.members || []).length} member{(g.members || []).length === 1 ? '' : 's'})
            </option>
          ))}
        </select>
      </div>

      {selectedGroup && (
        <div className="rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-[#F7FAFC] dark:bg-[#151F2C] p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-[#0A2342] dark:text-white">Members preview</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => startEditGroup(selectedGroup)}
                className="text-xs text-[#326AEB] dark:text-[#78B5FF] hover:underline"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={handleDeleteGroup}
                className="text-xs text-red-500 hover:underline"
              >
                Delete
              </button>
            </div>
          </div>
          <ul className="max-h-40 overflow-y-auto space-y-1.5">
            {(selectedGroup.members || []).map((m) => (
              <li
                key={`${m.email}-${m.role}`}
                className="text-xs text-[#6B7C93] dark:text-[#8FA3B8] flex justify-between gap-2"
              >
                <span className="truncate">{m.email}</span>
                <span className="shrink-0">{m.role}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!loadingGroups && groups.length === 0 && (
        <p className="text-xs text-[#6B7C93] dark:text-[#8FA3B8]">
          No groups yet. Create one to reuse the same users across projects.
        </p>
      )}
    </div>
  );

  const groupCreateContent = (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => {
          setGroupMode('apply');
          setEditingGroupId(null);
          setError('');
        }}
        className="text-xs font-medium text-[#326AEB] dark:text-[#78B5FF] hover:underline"
      >
        ← Back to select group
      </button>

      <div>
        <label className="block text-sm font-medium text-[#0A2342] dark:text-white mb-1.5">
          Group name
        </label>
        <input
          type="text"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          placeholder="e.g. QMetrix Core Team"
          className="w-full px-3 py-2.5 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB]"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-[#0A2342] dark:text-white mb-1.5">
          Description <span className="font-normal text-[#6B7C93]">(optional)</span>
        </label>
        <input
          type="text"
          value={groupDescription}
          onChange={(e) => setGroupDescription(e.target.value)}
          placeholder="Short note about this group"
          className="w-full px-3 py-2.5 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB]"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-medium text-[#0A2342] dark:text-white">
            Members
          </label>
          <button
            type="button"
            onClick={() => setMembers((prev) => [...prev, emptyMember(defaultRole)])}
            className="inline-flex items-center gap-1 text-xs font-medium text-[#326AEB] dark:text-[#78B5FF] hover:underline"
          >
            <Plus className="w-3.5 h-3.5" />
            Add row
          </button>
        </div>
        <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
          {members.map((member, index) => (
            <div key={`member-${index}`} className="flex items-center gap-2">
              <input
                type="email"
                value={member.email}
                onChange={(e) => updateMember(index, 'email', e.target.value)}
                placeholder="email@company.com"
                className="flex-1 min-w-0 px-2.5 py-2 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB]"
              />
              <select
                value={member.role}
                onChange={(e) => updateMember(index, 'role', e.target.value)}
                disabled={loadingRoles}
                className="w-36 shrink-0 px-2 py-2 rounded-md border border-[#D1E2F0] dark:border-[#25384F] bg-white dark:bg-[#151F2C] text-[#0A2342] dark:text-white text-sm outline-none focus:border-[#326AEB]"
              >
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() =>
                  setMembers((prev) =>
                    prev.length === 1 ? [emptyMember(defaultRole)] : prev.filter((_, i) => i !== index),
                  )
                }
                className="p-1.5 rounded text-[#6B7C93] hover:text-red-500 dark:text-[#8FA3B8]"
                title="Remove row"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <p className="mt-1.5 text-xs text-[#6B7C93] dark:text-[#8FA3B8]">
          Save the group once, then apply it to any project in this organisation.
        </p>
      </div>
    </div>
  );

  const groupContent = groupMode === 'create' ? groupCreateContent : groupApplyContent;

  const actions = (() => {
    if (mode === 'single') {
      return (
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSingleSubmit}
            disabled={busy || !email.trim() || !role || loadingRoles}
          >
            {assigning ? 'Sending...' : 'Add & Send Invite'}
          </Button>
        </div>
      );
    }

    if (groupMode === 'create') {
      return (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleSaveGroup({ alsoApply: false })}
            disabled={busy || !groupName.trim()}
          >
            {savingGroup ? 'Saving...' : editingGroupId ? 'Save group' : 'Save group only'}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleSaveGroup({ alsoApply: true })}
            disabled={busy || !groupName.trim()}
          >
            {savingGroup || assigning
              ? 'Working...'
              : editingGroupId
                ? 'Save & add to project'
                : 'Save & add to project'}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleApplyGroup}
          disabled={busy || !selectedGroupId}
        >
          {assigning ? 'Adding...' : 'Add group to project'}
        </Button>
      </div>
    );
  })();

  return (
    <Modal
      isOpen={isOpen}
      onClose={busy ? () => {} : onClose}
      title={`Add to ${projectName}`}
      size="medium"
      content={
        <div className="px-4 py-3 space-y-4">
          {allowUserGroups && (
            <div className="flex p-0.5 rounded-lg bg-[#F0F4F8] dark:bg-[#151F2C] border border-[#D1E2F0] dark:border-[#25384F]">
              <button
                type="button"
                onClick={() => {
                  setMode('single');
                  setError('');
                }}
                className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                  mode === 'single'
                    ? 'bg-white dark:bg-[#25384F] text-[#0A2342] dark:text-white shadow-sm font-medium'
                    : 'text-[#6B7C93] dark:text-[#8FA3B8]'
                }`}
              >
                Single user
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('group');
                  setError('');
                }}
                className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                  mode === 'group'
                    ? 'bg-white dark:bg-[#25384F] text-[#0A2342] dark:text-white shadow-sm font-medium'
                    : 'text-[#6B7C93] dark:text-[#8FA3B8]'
                }`}
              >
                User group
              </button>
            </div>
          )}

          {mode === 'single' || !allowUserGroups ? singleContent : groupContent}

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      }
      actions={actions}
    />
  );
};

AddEmployeeToProjectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  project: PropTypes.object,
  companyId: PropTypes.string,
  onInvite: PropTypes.func.isRequired,
  onApplyGroup: PropTypes.func,
  assigning: PropTypes.bool,
  allowUserGroups: PropTypes.bool,
};

AddEmployeeToProjectModal.defaultProps = {
  project: null,
  companyId: '',
  onApplyGroup: async () => {},
  assigning: false,
  allowUserGroups: false,
};

export default AddEmployeeToProjectModal;
